import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { recordPageVisit } from "@/lib/api";
import { Video, Play, Search, Clock, ExternalLink, Youtube } from "lucide-react";

// Real YouTube hospice training videos
const VIDEOS = [
  // End-of-Life Care
  {
    id: "WGpHNM2LH6o",
    title: "End of Life Care for Caregivers",
    description: "Comprehensive training on stages of dying, physical and behavioral changes, and supporting families through the end-of-life journey.",
    category: "end_of_life",
    duration: "25:00",
    source: "Bureau of Community and Health Systems",
    youtubeId: "WGpHNM2LH6o"
  },
  {
    id: "b8Eqw2RBRiQ",
    title: "Hospice Care Training Video 2025",
    description: "Overview of hospice care, team roles, symptom management, education for families, and signs and symptoms of dying.",
    category: "general",
    duration: "30:00",
    source: "SDAHO",
    youtubeId: "b8Eqw2RBRiQ"
  },
  {
    id: "CXuJc35-gf0",
    title: "Hospice 101 Webinar",
    description: "Learn what hospice is, differences from palliative care, eligibility requirements, and hospice benefits explained.",
    category: "general",
    duration: "45:00",
    source: "Parkinson Foundation WPA",
    youtubeId: "CXuJc35-gf0"
  },
  // Pain Management & Medications
  {
    id: "wnpM40zmwgY",
    title: "Understanding Morphine in Hospice Care",
    description: "Learn about comfort medications, morphine administration, and how it helps patients die peacefully without hastening death.",
    category: "medication",
    duration: "15:00",
    source: "Hospice Education",
    youtubeId: "wnpM40zmwgY"
  },
  {
    id: "6oZpAQW-bNA",
    title: "Hospice Pain Management Approach",
    description: "Understanding the multimodal approach to pain management including non-opioid and opioid medications.",
    category: "medication",
    duration: "20:00",
    source: "Pain Management Education",
    youtubeId: "6oZpAQW-bNA"
  },
  {
    id: "w2IuH6GX7so",
    title: "What Hospice Covers - Medications & Care",
    description: "Understanding what medications and services are covered under hospice benefit, including pain management and symptom control.",
    category: "general",
    duration: "12:00",
    source: "Hospice Information",
    youtubeId: "w2IuH6GX7so"
  },
  // Patient Care Skills
  {
    id: "8ITas_Eopl8",
    title: "Turning and Positioning Patients in Bed",
    description: "Proper techniques for safely repositioning patients to prevent pressure sores and maintain comfort.",
    category: "positioning",
    duration: "10:00",
    source: "CNA Training",
    youtubeId: "8ITas_Eopl8"
  },
  {
    id: "iYpZYa_WnjE",
    title: "Palliative Care Symptom Management",
    description: "Managing common symptoms including pain, breathing trouble, agitation, and other end-of-life concerns.",
    category: "comfort",
    duration: "18:00",
    source: "Palliative Care Education",
    youtubeId: "iYpZYa_WnjE"
  },
  {
    id: "3n_On_lbNo0",
    title: "Comfort Care Techniques",
    description: "Learn gentle techniques for providing comfort, reducing anxiety, and supporting patients at end of life.",
    category: "comfort",
    duration: "14:00",
    source: "Hospice Training",
    youtubeId: "3n_On_lbNo0"
  },
  // Communication & Emotional Support
  {
    id: "tINIAA5YAMc",
    title: "Communicating with Dying Patients",
    description: "Compassionate communication techniques for speaking with patients and families during the end-of-life journey.",
    category: "emotional",
    duration: "16:00",
    source: "End-of-Life Communication",
    youtubeId: "tINIAA5YAMc"
  },
  {
    id: "lB_oLnQnpXs",
    title: "Supporting Families Through Grief",
    description: "Understanding the grieving process and how to support family members before and after loss.",
    category: "emotional",
    duration: "22:00",
    source: "Grief Support Education",
    youtubeId: "lB_oLnQnpXs"
  },
  // Specialized Care
  {
    id: "xUBs2VcRuzg",
    title: "Subcutaneous Medication Administration",
    description: "Techniques for administering medications through subcutaneous routes when oral administration is not possible.",
    category: "medication",
    duration: "12:00",
    source: "Hospice Nursing Education",
    youtubeId: "xUBs2VcRuzg"
  },
];

const CATEGORIES = {
  all: "All Videos",
  general: "Hospice Basics",
  end_of_life: "End-of-Life Care",
  medication: "Medications",
  positioning: "Positioning",
  comfort: "Comfort Care",
  emotional: "Emotional Support"
};

export default function TrainingVideos() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    recordPageVisit("videos").catch(console.error);
  }, []);

  const filteredVideos = VIDEOS.filter(video => {
    const matchesCategory = activeCategory === "all" || video.category === activeCategory;
    const matchesSearch = video.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         video.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getYoutubeThumbnail = (youtubeId) => {
    return `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`;
  };

  return (
    <div className="min-h-screen" data-testid="training-videos-page">
      {/* Hero */}
      <section className="relative section-padding pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 to-background" />
        <div className="container-max relative">
          <div className="max-w-3xl">
            <Badge variant="secondary" className="mb-4">
              <Youtube className="w-3 h-3 mr-1" />
              Video Library
            </Badge>
            <h1 className="heading-1 text-foreground mb-6">
              Training <span className="gradient-text">Videos</span>
            </h1>
            <p className="body-lg text-muted-foreground mb-8">
              Watch expert-led videos on essential hospice caregiving skills. From pain management 
              to comfort care techniques, learn from trusted healthcare sources.
            </p>
            
            {/* Search */}
            <div className="relative max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="Search videos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 rounded-xl bg-white/80 backdrop-blur"
                data-testid="video-search"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Videos Grid */}
      <section className="section-padding pt-8 bg-muted/30">
        <div className="container-max">
          {/* Category Tabs */}
          <Tabs value={activeCategory} onValueChange={setActiveCategory} className="mb-8">
            <TabsList className="bg-white shadow-soft flex-wrap h-auto p-2 gap-2">
              {Object.entries(CATEGORIES).map(([key, label]) => (
                <TabsTrigger 
                  key={key} 
                  value={key}
                  className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-white"
                  data-testid={`category-tab-${key}`}
                >
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* Video Count */}
          <p className="text-sm text-muted-foreground mb-6">
            Showing {filteredVideos.length} video{filteredVideos.length !== 1 ? 's' : ''}
          </p>

          {/* Video Grid */}
          {filteredVideos.length === 0 ? (
            <div className="text-center py-12">
              <Video className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No videos found matching your search.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredVideos.map((video) => (
                <Card 
                  key={video.id} 
                  className="glass-card overflow-hidden card-hover cursor-pointer border-0"
                  onClick={() => setSelectedVideo(video)}
                  data-testid={`video-card-${video.id}`}
                >
                  <div className="relative aspect-video">
                    <img 
                      src={getYoutubeThumbnail(video.youtubeId)} 
                      alt={video.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center">
                        <Play className="w-8 h-8 text-red-600 ml-1" />
                      </div>
                    </div>
                    <Badge className="absolute top-3 left-3 bg-red-600 text-white border-0">
                      <Youtube className="w-3 h-3 mr-1" />
                      YouTube
                    </Badge>
                    <Badge className="absolute top-3 right-3 bg-black/60 text-white border-0">
                      <Clock className="w-3 h-3 mr-1" />
                      {video.duration}
                    </Badge>
                  </div>
                  <CardContent className="p-4">
                    <Badge variant="outline" className="mb-2 text-xs">
                      {CATEGORIES[video.category]}
                    </Badge>
                    <h3 className="font-serif font-medium text-foreground mb-1 line-clamp-1">{video.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{video.description}</p>
                    <p className="text-xs text-muted-foreground">Source: {video.source}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Video Modal */}
      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className="max-w-4xl" data-testid="video-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">{selectedVideo?.title}</DialogTitle>
          </DialogHeader>
          <div className="aspect-video bg-black rounded-xl overflow-hidden">
            {selectedVideo && (
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${selectedVideo.youtubeId}?autoplay=1`}
                title={selectedVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            )}
          </div>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="text-muted-foreground mb-2">{selectedVideo?.description}</p>
              <p className="text-sm text-muted-foreground">Source: {selectedVideo?.source}</p>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open(`https://www.youtube.com/watch?v=${selectedVideo?.youtubeId}`, '_blank')}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open on YouTube
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Additional Resources */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <h2 className="heading-3 text-foreground mb-6 text-center">More Training Resources</h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Card className="text-center p-6">
              <h3 className="font-medium mb-2">Hospice of the Western Reserve</h3>
              <p className="text-sm text-muted-foreground mb-4">Comprehensive caregiver training video playlist</p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => window.open('https://www.youtube.com/playlist?list=PLlxD8HEDZSL5R83b7hWzjA_ZVB0tl2iCu', '_blank')}
              >
                <Youtube className="w-4 h-4 mr-2 text-red-600" />
                View Playlist
              </Button>
            </Card>
            <Card className="text-center p-6">
              <h3 className="font-medium mb-2">HCA Hospice Training</h3>
              <p className="text-sm text-muted-foreground mb-4">Hand-washing, infection control, and practical topics</p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => window.open('https://www.youtube.com/playlist?list=PLi69r_4xPrPQjUyFavZTjMhW0OLSsAJPV', '_blank')}
              >
                <Youtube className="w-4 h-4 mr-2 text-red-600" />
                View Playlist
              </Button>
            </Card>
            <Card className="text-center p-6">
              <h3 className="font-medium mb-2">Community Hospice Videos</h3>
              <p className="text-sm text-muted-foreground mb-4">Pain management, breathing, agitation, and comfort</p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => window.open('https://www.communityhospice.com/caregiver-educational-videos/', '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Visit Website
              </Button>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary/5">
        <div className="container-max text-center">
          <div className="max-w-2xl mx-auto space-y-6">
            <h2 className="heading-2 text-foreground">Have Questions About Caregiving?</h2>
            <p className="body-base text-muted-foreground">
              Our AI assistant can provide personalized guidance on caregiving techniques 
              and answer your specific questions.
            </p>
            <Button asChild className="rounded-full px-8 py-6">
              <a href="/chat">Ask Our AI Assistant</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
